<script setup lang="ts">
import AppAside from "@/components/AppAside.vue";

import {
  usePlaceMenuStore,
  usePlaceReviewsStore,
  usePlaceStore,
} from "@/modules/place-module";
import { onUnmounted, computed, onBeforeMount, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import PlaceSkeleton from "./components/PlaceSkeleton.vue";
import PlaceInfo from "./components/PlaceInfo/PlaceInfo.vue";
import PlaceProduct from "./components/PlaceProduct/PlaceProduct.vue";
import PlacePaymentModal from "./components/PlaceModals/PlacePaymentModal.vue";
import PlaceCashbackModal from "./components/PlaceModals/PlaceCashbackModal.vue";
import PlaceFeedbackModal from "./components/PlaceModals/PlaceFeedbackModal.vue";
import PlaceYandexTermsModal from "./components/PlaceModals/PlaceYandexTermsModal.vue";
import { UiBottomSheet } from "@packages/component-library";
import PlaceFeedbackContacts from "./components/PlaceInfo/PlaceFeedbackContacts.vue";
import { useI18n } from "vue-i18n";

const emit = defineEmits(["favoriteUpdate"]);
const route = useRoute();
const router = useRouter();
const placeStore = usePlaceStore();
const placeMenuStore = usePlaceMenuStore();
const placeReviewsStore = usePlaceReviewsStore();
const place = computed(() => placeStore.place);
const showFeedbackModal = ref(false);
const feedBackValue = ref(0);
const showFeedbackContacts = ref(false);
const showYandexTermsModal = ref(false);
const showCashbackModal = ref(false);
const showPaymentModal = ref(false);
const { t } = useI18n();

const ratePlace = (value: number) => {
  feedBackValue.value = value;
  showFeedbackModal.value = true;
};

const closeFeedbackModal = (value: number) => {
  feedBackValue.value = value;
  showFeedbackModal.value = false;
};

const closePlace = () => {
  router.push({ name: route.name });
};

onBeforeMount(async () => {
  if (!place.value) {
    try {
      await placeStore.loadPlaceInfo(route.query.place);
    } catch (e) {
      await router.push({ name: route.name });
    }
  }
});

onUnmounted(() => {
  placeStore.clearPlace();
  placeMenuStore.clearPlaceMenu();
  placeReviewsStore.clearPlaceReviews();
});
</script>

<template>
  <UiBottomSheet
    v-if="showFeedbackContacts"
    v-model="showFeedbackContacts"
    :label="t('place.info.contact')"
    @update:modelValue="showFeedbackContacts = false"
  >
    <template #default>
      <PlaceFeedbackContacts />
    </template>
  </UiBottomSheet>

  <PlaceFeedbackModal
    v-if="showFeedbackModal"
    :rate="feedBackValue"
    @close="closeFeedbackModal"
  />

  <PlaceYandexTermsModal
    v-if="showYandexTermsModal"
    @close="showYandexTermsModal = false"
  />

  <PlaceCashbackModal
    v-if="showCashbackModal"
    @close="showCashbackModal = false"
  />

  <PlacePaymentModal
    v-if="showPaymentModal"
    @close="showPaymentModal = false"
  />

  <AppAside @close="closePlace">
    <div class="place-aside">
      <template v-if="place">
        <PlaceInfo
          v-if="!route.query.product"
          @ratePlace="ratePlace"
          @showPaymentModal="showPaymentModal = true"
          @showYandexTermsModal="showYandexTermsModal = true"
          @showCashbackModal="showCashbackModal = true"
          @favoriteUpdate="emit('favoriteUpdate')"
          @showFeedbackContacts="showFeedbackContacts = true"
        />
        <PlaceProduct v-else />
      </template>

      <PlaceSkeleton v-else />
    </div>
  </AppAside>
</template>

<style scoped lang="scss">
.place-aside {
  height: 100%;
}
</style>
